import SvgIcon from './src/SvgIcon.vue';
import Page404 from './src/404.vue';
import SliderVerify from './src/slider-verify/SliderVerify.vue';
import SmsButton from './src/sms-button/SmsButton.vue';
import Footer from './src/Footer.vue';
import NumberRangeInput from './src/number-range-input/NumberRangeInput.vue';
import SecurePassword from './src/secure-password-input/SecurePassword.vue';
import TableRowButtons from './src/table-row-buttons/TableRowButtons.vue';
import CardImageUpload from './src/card-image-upload/CardImageUpload.vue';
import OfficePreview from './src/office-previewer/OfficePreview.vue';
import CustomTag from './src/custom-tag/index.vue';
import CheckService from './src/check-service/index.vue';
import Steps from './src/steps/Steps.vue';
import AreaSelect from './src/AreaSelect.vue';
import DraggerRobot from './src/DraggerRobot.vue';
import ImageEditor from './src/image-editor/ImageEditor.vue';
import CustomScrollbar from './src/custom-scrollbar/index.vue';
import GuideEntry from './src/guide-entry/GuideEntry.vue';
import GuideDesc from './src/guide-entry/GuideDesc.vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getHostbaseUrl } from '@core/tools';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { VNode } from 'vue';

export {
  Steps,
  AreaSelect,
  SvgIcon,
  Page404,
  SliderVerify,
  SmsButton,
  Footer,
  TableRowButtons,
  SecurePassword,
  NumberRangeInput,
  CardImageUpload,
  OfficePreview,
  CustomTag,
  CheckService,
  ImageEditor,
  CustomScrollbar,
  DraggerRobot,
  GuideEntry,
  GuideDesc,
};

export * from './src/table-row-buttons/getTableButtons';
export * from './src/not-support-browser';
export * from './src/hooks';
export * from './src/card-image-upload/uploadApi';
export * from './src/guide-entry/guideEntries';
export * from './src/guide-entry/guideDescriptions';

export function onConfirm({
  title = '提示',
  message,
  okText = '确定',
  cancelText = '取消',
  showClose = true,
  closeOnClickModal = false,
  showCancelButton = true,
  dangerouslyUseHTMLString = false,
  type = '',
  onOk,
  onCancel = () => {},
}: {
  /**
   * 标题
   * @default '提示'
   */
  title?: string;
  /** 提示内容 */
  message: string | VNode;
  okText?: string;
  cancelText?: string;
  showClose?: boolean;
  showCancelButton?: boolean;
  closeOnClickModal?: boolean;
  type?: '' | 'success' | 'warning' | 'info' | 'error';
  /** 是否使用HTML字符串 */
  dangerouslyUseHTMLString?: boolean;
  /** 确认事件 */
  onOk?: () => Promise<any>;
  /** 取消事件 */
  onCancel?: () => void;
}) {
  ElMessageBox(
    {
      title,
      message,
      type,
      showCancelButton,
      confirmButtonText: okText,
      cancelButtonText: cancelText,
      closeOnClickModal,
      showClose,
      dangerouslyUseHTMLString,
      beforeClose: async (action, instance, done) => {
        if (action === 'confirm') {
          instance.confirmButtonLoading = true;
          try {
            if (onOk) await onOk();
            done();
          } catch (error) {
            if ((error as Error).message) ElMessage.error((error as Error).message);
          }
          instance.confirmButtonLoading = false;
        } else {
          onCancel();
          done();
        }
      },
    },
  );
}

const hostUrl = getHostbaseUrl();

/**
 * 获取安全密码控件的随机因子数据（使用示例如下）
 *
 * ```ts
 * const randomKeys = ref({} as IRandomKeys);
 * const passObj = ref({} as ISecurePassword);
 *
 * async function fetchRandomKeys() {
 *   try {
 *     randomKeys.value = await queryRandomPassKeys({ propertyValue: 5019254166528 });
 *   } catch (error) {
 *     //
 *   }
 * }
 *
 * function onPassChange({ password, hash }: ISecurePassword) {
 *   passObj.value = {
 *     password,
 *     hash,
 *   };
 * }
 *
 * fetchRandomKeys();
 * ```
 *
 * ```jsx
 * // 组件使用
 * <secure-password
 *   name="password"
 *   placeholder="请再次输入支付密码"
 *   :random-keys="randomKeys"
 *   v-on:change="onPassChange"
 * />
 * ```
 */
export async function queryRandomPassKeys({
  prefix = '',
  propertyId = 'lianId',
  propertyValue,
  apiMethod = 'get',
}: {
  /**
   * 接口前缀
   * @description 比如： 'api/company/lian/getRandom'
  */
  prefix?: string;
  /**
   * 入参的属性名称
   * @default 'lianId'
   */
  propertyId?: string;
  /** 入参【propertyId】的值 */
  propertyValue: number;
  /**
   * 请求方法
   * @default 'get'
   */
  apiMethod?: 'get' | 'post';
}) {
  let keys = {} as IRandomKeys;

  try {
    const hostname = location.hostname.split('.').slice(1)
      .join('.') || 'localhost';

    const { code, context, message } = await Http[apiMethod]<any>(`${hostUrl}/${prefix}`, {
      appName: hostname,
      pkgName: hostname,
      [propertyId]: propertyValue,
    });

    if (code === EAxiosResponseCode.Succeed) {
      keys = context || {} as IRandomKeys;
    } else if (message) {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error('获取随机因子失败: ' + getHttpErrorMessage(error));
  }

  return keys;
}

export interface ISecurePassowrdInputOption {
  /** 随机key */
  randomValue?: string;
  /** 国密加密必须参数，RSA加密可不要 */
  sm2KeyHex?: string;
  /** RSA加密必须参数，国密加密可不要 */
  rsaPublicContent?: string;
  /** license, 必须参数 */
  license?: string;
}

export interface IRandomKeys {
  oid_partner?: string;
  license?: string;
  randomKey?: string;
  randomValue?: string;
  ret_code?: string;
  ret_msg?: string;
  sm2KeyHex?: string;
  user_id?: string;
  mapArr?: string;
  rsaPublicContent?: string;
}

export interface ISecurePassword {
  /** 加密密码对应的hash，用于判断密码是否一致 */
  hash: string;
  /** 加密后的密码 */
  password: string;
  length?: number;
}

export interface IStepItem<T extends string = string> {
  key: T;
  title: string;
  describe?: string;
}
